'use client'

import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import {
  ChevronRightIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/16/solid'
import { cn } from '../../helpers'

const JosefBreadcrumb = React.forwardRef<
  HTMLElement,
  React.ComponentPropsWithoutRef<'nav'> & {
    separator?: React.ReactNode
  }
>(({ ...props }, ref) => <nav ref={ref} aria-label="breadcrumb" {...props} />)
JosefBreadcrumb.displayName = 'JosefBreadcrumb'

const JosefBreadcrumbList = React.forwardRef<
  HTMLOListElement,
  React.ComponentPropsWithoutRef<'ol'>
>(({ className, ...props }, ref) => (
  <ol
    ref={ref}
    className={cn(
      'flex flex-wrap items-center gap-2 break-words text-sm text-black/60 dark:text-white/60',
      className
    )}
    {...props}
  />
))
JosefBreadcrumbList.displayName = 'JosefBreadcrumbList'

const JosefBreadcrumbItem = React.forwardRef<
  HTMLLIElement,
  React.ComponentPropsWithoutRef<'li'>
>(({ className, ...props }, ref) => (
  <li
    ref={ref}
    className={cn('inline-flex items-center gap-2', className)}
    {...props}
  />
))
JosefBreadcrumbItem.displayName = 'JosefBreadcrumbItem'

const JosefBreadcrumbLink = React.forwardRef<
  HTMLAnchorElement,
  React.ComponentPropsWithoutRef<'a'> & {
    asChild?: boolean
  }
>(({ asChild, className, ...props }, ref) => {
  const Comp = asChild ? Slot : 'a'

  return (
    <Comp
      ref={ref}
      className={cn(
        'transition-colors hover:text-black dark:hover:text-white',
        className
      )}
      {...props}
    />
  )
})
JosefBreadcrumbLink.displayName = 'JosefBreadcrumbLink'

const JosefBreadcrumbPage = React.forwardRef<
  HTMLSpanElement,
  React.ComponentPropsWithoutRef<'span'>
>(({ className, ...props }, ref) => (
  <span
    ref={ref}
    role="link"
    aria-disabled="true"
    aria-current="page"
    className={cn('font-medium text-black dark:text-white', className)}
    {...props}
  />
))
JosefBreadcrumbPage.displayName = 'JosefBreadcrumbPage'

const JosefBreadcrumbSeparator = ({
  children,
  className,
  ...props
}: React.ComponentProps<'li'>) => (
  <li
    role="presentation"
    aria-hidden="true"
    className={cn('[&>svg]:size-4', className)}
    {...props}
  >
    {children ?? <ChevronRightIcon className="size-4" />}
  </li>
)
JosefBreadcrumbSeparator.displayName = 'JosefBreadcrumbSeparator'

const JosefBreadcrumbEllipsis = ({
  className,
  ...props
}: React.ComponentProps<'span'>) => (
  <span
    role="presentation"
    aria-hidden="true"
    className={cn('flex h-9 w-9 items-center justify-center', className)}
    {...props}
  >
    <EllipsisHorizontalIcon className="size-4" />
    <span className="sr-only">More</span>
  </span>
)
JosefBreadcrumbEllipsis.displayName = 'JosefBreadcrumbEllipsis'

export {
  JosefBreadcrumb,
  JosefBreadcrumbList,
  JosefBreadcrumbItem,
  JosefBreadcrumbLink,
  JosefBreadcrumbPage,
  JosefBreadcrumbSeparator,
  JosefBreadcrumbEllipsis,
}
